/**
 * The App level types and enum are all defined in this files
 */
export type AppProviderProps = {
  children: React.ReactNode;
};

export interface Obj {
  [key: string]: any;
}

/**
 * The enum type is used while calling the API services like fetcher, mutator, batchMutator
 */
export enum NetworkMethodEnum {
  POST = "POST",
  PUT = "PUT",
  PATCH = "PATCH",
  DELETE = "DELETE",
}

export interface IApiService {
  batchMutator: (identifier: string | number, data: string) => Promise<any>;
  fetcher: (url: string, pageSize?: number) => Promise<any>;
  mutator: (method: NetworkMethodEnum, url: string, data?: Obj) => Promise<any>;
}

export interface IBatchPayload {
  method: NetworkMethodEnum;
  url: string;
  payload: Obj;
}

export interface StringMap {
  "@odata.etag": string;
  "displayorder@OData.Community.Display.V1.FormattedValue": string;
  displayorder: number;
  value: string;
  attributename: string;
  "versionnumber@OData.Community.Display.V1.FormattedValue": string;
  versionnumber: number;
  "langid@OData.Community.Display.V1.FormattedValue": string;
  langid: number;
  "objecttypecode@OData.Community.Display.V1.FormattedValue": string;
  objecttypecode: string;
  stringmapid: string;
  organizationid: string;
  "attributevalue@OData.Community.Display.V1.FormattedValue": string;
  attributevalue: number;
  [key: string]: any;
}

export interface IFormSelectOptionsApiRes {
  "@odata.context": string;
  value: StringMap[];
}

export interface ICommonListApiRes<T> {
  "@odata.context": string;
  "@odata.count": number;
  "@Microsoft.Dynamics.CRM.totalrecordcountlimitexceeded"?: boolean;
  "@odata.nextLink"?: string;
  value: T[];
}

export interface ICommonFormProps<T> {
  drawerOpen: boolean;
  isViewMode: boolean;
  currentRecord?: T;
}

export interface ICommonTableProps<T> {
  isLoading: boolean;
  data?: T[];
  current?: number;
  pageSize?: number;
  total?: number;
  nextLink?: string;
  totalRecordCountLimitExceeded?: boolean;
  handleView: (detail: T) => void;
  handleUpdate: (detail: T) => void;
  handleDelete: (detailId: string) => void;
  handleChange?: (page: number, pageSize: number) => void;
  handleRowSelectionChange?: (
    selectedRowKeys: React.Key[],
    selectedRows?: object[],
  ) => void;
}

export interface SelectInputOption {
  value: string | number;
  label: React.ReactNode;
  [key: string]: any;
}

export interface IFormSelectOptions {
  [key: string]: Array<SelectInputOption>;
}

export enum PlacementEnum {
  top = "top",
  right = "right",
  bottom = "bottom",
  left = "left",
}

export enum DrawerSizeEnum {
  small = "sm",
  medium = "md",
  large = "lg",
}

export enum SizeEnum {
  small = "small",
  medium = "medium",
  large = "large",
}

export enum SelectModeEnum {
  multiple = "multiple",
  tags = "tags",
}

export enum ConfirmationTypeEnum {
  success = "success",
  warning = "warning",
  danger = "danger",
  primary = "primary",
}

export interface IStepProps {
  title: string;
  content?: React.ReactNode;
  description?: string;
  icon?: React.ReactNode;
}

export interface ICommonQueryParams {
  current: number;
  pageSize: number;
  filter?: string;
  nextSkipToken?: string;
}

export interface IAppRole {
  "@odata.etag"?: string;
  dogma_approleid: string;
  dogma_name: string;
  dogma_permissionsjson?: string | null;
  dogma_defaultsecurityrole?: boolean | null;
  "dogma_defaultsecurityrole@OData.Community.Display.V1.FormattedValue"?: string;
  statecode?: number;
  "statecode@OData.Community.Display.V1.FormattedValue"?: string;
  statuscode?: number;
  "statuscode@OData.Community.Display.V1.FormattedValue"?: string;
  [key: string]: any;
}

export interface IUser {
  "@odata.etag"?: string;
  systemuserid: string;
  domainname?: string;
  firstname?: string;
  lastname?: string;
  fullname?: string;
  statecode: number;
  dogma_team?: string;
  dogma_region?: number;
  dogma_AppRole_SystemUser_SystemUser?: IAppRole[];
  "dogma_region@OData.Community.Display.V1.FormattedValue": string;
  "dogma_team@OData.Community.Display.V1.FormattedValue": string;
  internalemailaddress?: string;
  businessunitid?: string;
  "businessunitid@OData.Community.Display.V1.FormattedValue"?: string;
  isdisabled?: boolean;
  "isdisabled@OData.Community.Display.V1.FormattedValue"?: string;
  dogma_userstatus?: number;
  createdon?: string;
  "createdon@OData.Community.Display.V1.FormattedValue"?: string;
  modifiedon?: string;
  "modifiedon@OData.Community.Display.V1.FormattedValue"?: string;
  [key: string]: any;
}
// User List Response (OData format)
export interface IUserListResponse {
  "@odata.context": string;
  "@odata.count"?: number;
  "@odata.nextLink"?: string;
  "@Microsoft.Dynamics.CRM.totalrecordcountlimitexceeded"?: boolean;
  value: IUser[];
}

export interface IUserListTransformResponse extends IUserListResponse {
  defaultRole?: IAppRole;
  customRole?: IAppRole;
  permission: {
    [permission: string]: boolean;
  };
  currentUser?: IUser;
}
